/**
 * The message box used by the chat composers (channel / DM thread, thread
 * replies, the Inbox conversation). It starts one line tall and grows with what
 * is typed, pasted or inserted (Insert link, @mentions), up to a cap — about ten
 * lines, or 40% of the conversation pane when that is smaller — after which it
 * scrolls inside itself. It shrinks back once the value is cleared (send).
 *
 * Keys (Slack-style): Enter sends, Shift+Enter starts a new line, Cmd/Ctrl+Enter
 * also sends. A key pressed while an IME is composing (Japanese, Chinese, Korean
 * input, some dictation) belongs to the IME and never sends. The caller's own
 * onKeyDown runs first; calling `e.preventDefault()` there (mention picker,
 * Insert link shortcut) keeps Enter from sending.
 *
 * Enter sends on every device: the codebase has no mobile-detection convention,
 * and each composer has a visible Send button.
 *
 * The pane the 40% cap is measured against is the nearest ancestor marked
 * `data-composer-pane`; without one, the window height is used.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';

export const COMPOSER_MAX_ROWS = 10;
export const COMPOSER_MAX_PANE_FRACTION = 0.4;

/** True while an IME owns the keystroke (composition in progress). */
export function isImeComposing(e: React.KeyboardEvent): boolean {
  const native = e.nativeEvent as KeyboardEvent | undefined;
  return Boolean(native?.isComposing) || e.keyCode === 229;
}

/** Enter (no Shift) or Cmd/Ctrl+Enter, outside IME composition. */
export function isSendKey(e: React.KeyboardEvent): boolean {
  if (e.key !== 'Enter' || isImeComposing(e)) return false;
  if (e.metaKey || e.ctrlKey) return true;
  return !e.shiftKey && !e.altKey;
}

function px(value: string | null | undefined): number {
  const n = parseFloat(value || '');
  return Number.isFinite(n) ? n : 0;
}

/** The largest height (border-box px) the box may grow to. */
export function composerMaxHeight(el: HTMLTextAreaElement, maxRows: number, paneFraction: number): number {
  const cs = window.getComputedStyle(el);
  const fontSize = px(cs.fontSize) || 14;
  const lineHeight = px(cs.lineHeight) || Math.round(fontSize * 1.43);
  const chrome = px(cs.paddingTop) + px(cs.paddingBottom) + px(cs.borderTopWidth) + px(cs.borderBottomWidth);
  const byRows = maxRows * lineHeight + chrome;
  const pane = el.closest('[data-composer-pane]') as HTMLElement | null;
  const paneHeight = pane?.clientHeight || window.innerHeight || 0;
  const byPane = paneHeight > 0 ? paneHeight * paneFraction : Infinity;
  // Never cap below a single line.
  return Math.max(lineHeight + chrome, Math.min(byRows, byPane));
}

export interface AutoGrowTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value'> {
  value: string;
  /** Called for Enter / Cmd-Ctrl+Enter (unless the caller's onKeyDown prevented it). */
  onSubmit?: () => void;
  maxRows?: number;
  maxPaneFraction?: number;
}

export const AutoGrowTextarea = React.forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  (
    {
      value,
      onSubmit,
      onKeyDown,
      maxRows = COMPOSER_MAX_ROWS,
      maxPaneFraction = COMPOSER_MAX_PANE_FRACTION,
      className,
      style,
      rows = 1,
      ...props
    },
    forwardedRef,
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const setRef = React.useCallback(
      (el: HTMLTextAreaElement | null) => {
        innerRef.current = el;
        if (typeof forwardedRef === 'function') forwardedRef(el);
        else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
      },
      [forwardedRef],
    );

    const resize = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      const max = composerMaxHeight(el, maxRows, maxPaneFraction);
      el.style.maxHeight = `${max}px`;
      // Measure the content at its natural height, then settle on min(content, cap).
      el.style.height = 'auto';
      const cs = window.getComputedStyle(el);
      const borders = cs.boxSizing === 'border-box' ? px(cs.borderTopWidth) + px(cs.borderBottomWidth) : 0;
      const content = el.scrollHeight + borders;
      const next = Math.min(content, max);
      el.style.height = `${next}px`;
      el.style.overflowY = content > max + 1 ? 'auto' : 'hidden';
    }, [maxRows, maxPaneFraction]);

    // Every value change (typing, paste, Insert link, mention, clear on send).
    React.useLayoutEffect(() => {
      resize();
    }, [value, resize]);

    // The cap follows the pane / window size.
    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      window.addEventListener('resize', resize);
      return () => window.removeEventListener('resize', resize);
    }, [resize]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // The IME owns keys mid-composition: no send, no shortcuts.
      if (isImeComposing(e)) return;
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (onSubmit && isSendKey(e)) {
        e.preventDefault();
        onSubmit();
      }
    };

    return (
      <textarea
        ref={setRef}
        value={value}
        rows={rows}
        onKeyDown={handleKeyDown}
        className={cn(
          'block w-full resize-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm leading-5 text-gray-900 dark:text-gray-100 ring-offset-white dark:ring-offset-gray-900 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:focus-visible:ring-teal-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        style={style}
        data-testid="composer-textarea"
        {...props}
      />
    );
  },
);
AutoGrowTextarea.displayName = 'AutoGrowTextarea';

export default AutoGrowTextarea;
